import { ResolverTag } from '@ps/web-core-enums';
import { Translation } from '@ps/web-shared-components';
import { StarsLink } from '@ps/web-shared-ui';
import type { Error, Validator } from '@ps/web-shared-validators';
import { minLength, numbersOnly, required } from '@ps/web-shared-validators';
import { format } from 'date-fns';
import React from 'react';
import { useSelector } from 'react-redux';
import { lastValueFrom } from 'rxjs';

import useOpenWindow from '@swf/core/hooks/useOpenWindow';
import useRequest from '@swf/core/hooks/useRequest';
import { select } from '@swf/core/store';

import CNPIcon from '../../../../../../assets/icons/cnp.svg';
import InfoPrompt from '../../../../../InfoPrompt/InfoPrompt';
import TextInput from '../../../../../TextInput/TextInput';

import {
  TXTCLI_Info,
  TXTWCL_CNP,
  TXTWCL_Ensure_your_CNP_matches_your_d_ELL,
  TXTWCL_Invalid_number,
  TXTWCL_Please_log_in_to_an_existing_X_ELL,
  TXTWCL_The_CNP_is_already_registered__ELL,
  TXTWCL_Whole_numbers_onlyX_No_decimal_ELL,
  TXTWCL_You_may_only_own_a_single_Star_ELL,
  TXTWCL_Your_CNP_must_be_13_digitsX,
  TXTWCL_Your_CNP_number_is_found_on_yo_ELL,
  TXTWCL_Your_date_of_birth_and_CNP_mus_ELL
} from './CNP.messages.json';
import { infoPrompt, labelContent } from './CNP.scss';

const validateCNPDob =
  (dob?: Date, message?: Error['message']) =>
  (value: string): Error => ({
    valid: value?.substring(1, 7) === format(new Date(dob as Date), 'yyMMdd'),
    message
  });

const validationROCheckDigit = (value: string) => {
  const RO_CONTROL_DIGITS = '279146358279',
    mod =
      value
        .substring(0, value.length - 1)
        .split('')
        .map(
          (char: string, index: number) =>
            (char.charCodeAt(0) - 48) * (RO_CONTROL_DIGITS.charCodeAt(index) - 48)
        )
        .reduce((a, b) => a + b) % 11;
  return value.slice(-1).charCodeAt(0) === (mod === 10 ? 1 : mod) + 48;
};

const validateCNPCheckDigit =
  (message?: Error['message']) =>
  (value: string): Error => ({
    valid: value?.length === 13 ? validationROCheckDigit(value) : undefined,
    message
  });

const isROCNPNumerical = (value: string) => /^\d+$/.test(value) && value?.length === 13;
const CNP: React.FC = () => {
  const dob = useSelector(select.account.getDateOfBirth);
  const options = useSelector(select.cms.getRequestOptions);
  const request = useRequest();

  const checkCNP = (message: Error['message']) => {
    const isUnique: Validator = async (value: string): Promise<Error> => {
      if (value && isROCNPNumerical(value)) {
        const {
          body: { unique }
        } = await lastValueFrom(
          request({
            ...options,
            service: 'wc3.user.check.fiscalCode',
            body: { fiscalCode: value }
          })
        );
        return {
          valid: unique,
          message
        };
      } else {
        return {
          valid: false,
          message
        };
      }
    };
    return isUnique as Validator;
  };

  const openWindowContactSupport = useOpenWindow({
    resolverTag: ResolverTag.ContactSupport,
    centered: true
  });

  return (
    // field has missing property "checkDisabled" because of problem described in PAMT-25470
    <TextInput
      id="nationalDocumentNumber"
      inputMode="numeric"
      type="tel"
      maxLength={13}
      label={TXTWCL_CNP}
      labelClassName={labelContent}
      labelContent={
        <InfoPrompt title={{ translation: TXTCLI_Info }}>
          <div className={infoPrompt}>
            <p>
              <Translation {...TXTWCL_Your_CNP_number_is_found_on_yo_ELL} />{' '}
              <Translation {...TXTWCL_Your_date_of_birth_and_CNP_mus_ELL} />
            </p>
            <CNPIcon />
          </div>
        </InfoPrompt>
      }
      validators={[
        required(null),
        numbersOnly(TXTWCL_Whole_numbers_onlyX_No_decimal_ELL),
        minLength(13, TXTWCL_Your_CNP_must_be_13_digitsX),
        validateCNPDob(dob, TXTWCL_Ensure_your_CNP_matches_your_d_ELL),
        validateCNPCheckDigit(TXTWCL_Invalid_number),
        checkCNP([
          TXTWCL_The_CNP_is_already_registered__ELL,
          TXTWCL_You_may_only_own_a_single_Star_ELL,
          {
            ...TXTWCL_Please_log_in_to_an_existing_X_ELL,
            values: {
              a: (msg: React.ReactNode) => (
                <StarsLink onClick={openWindowContactSupport}>{msg}</StarsLink>
              )
            }
          }
        ])
      ]}
    />
  );
};

export default CNP;
